type ZipDataInput = ArrayBuffer | Uint8Array

export type ClientZipFile = {
  name: string
  data: ZipDataInput
}

function u16(n: number) {
  const b = new Uint8Array(2)
  b[0] = n & 255
  b[1] = (n >>> 8) & 255
  return b
}

function u32(n: number) {
  const b = new Uint8Array(4)
  b[0] = n & 255
  b[1] = (n >>> 8) & 255
  b[2] = (n >>> 16) & 255
  b[3] = (n >>> 24) & 255
  return b
}

function concat(chunks: Uint8Array[]) {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0)
  const out = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}

function toBytes(data: ZipDataInput) {
  return data instanceof Uint8Array ? data : new Uint8Array(data)
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i += 1) {
    let c = i
    for (let k = 0; k < 8; k += 1) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1)
    table[i] = c >>> 0
  }
  return table
})()

function crc32(buf: Uint8Array) {
  let c = 0 ^ -1
  for (let i = 0; i < buf.length; i += 1) c = (c >>> 8) ^ CRC_TABLE[(c ^ buf[i]) & 0xFF]
  return (c ^ -1) >>> 0
}

function dosDateTime(d = new Date()) {
  const year = d.getFullYear()
  const month = d.getMonth() + 1
  const day = d.getDate()
  const hours = d.getHours()
  const minutes = d.getMinutes()
  const seconds = Math.floor(d.getSeconds() / 2)
  const dosTime = (hours << 11) | (minutes << 5) | seconds
  const dosDate = ((year - 1980) << 9) | (month << 5) | day
  return { dosTime, dosDate }
}

export function createStoredZip(files: ClientZipFile[]) {
  const localFiles: { name: string, crc: number, size: number, off: number, dt: ReturnType<typeof dosDateTime> }[] = []
  let offset = 0
  const outChunks: Uint8Array[] = []

  for (const file of files) {
    const data = toBytes(file.data)
    const crc = crc32(data)
    const size = data.length
    const dt = dosDateTime(new Date())
    const nameBytes = new TextEncoder().encode(file.name)
    const header = concat([
      u32(0x04034b50),
      u16(20), u16(0), u16(0),
      u16(dt.dosTime), u16(dt.dosDate),
      u32(crc), u32(size), u32(size),
      u16(nameBytes.length), u16(0),
      nameBytes
    ])

    outChunks.push(header, data)
    localFiles.push({ name: file.name, crc, size, off: offset, dt })
    offset += header.length + data.length
  }

  const cdStart = offset
  const cdChunks: Uint8Array[] = []
  let cdSize = 0
  for (const file of localFiles) {
    const nameBytes = new TextEncoder().encode(file.name)
    const cd = concat([
      u32(0x02014b50),
      u16(20), u16(20),
      u16(0), u16(0),
      u16(file.dt.dosTime), u16(file.dt.dosDate),
      u32(file.crc), u32(file.size), u32(file.size),
      u16(nameBytes.length), u16(0), u16(0),
      u16(0), u16(0),
      u32(0),
      u32(file.off),
      nameBytes
    ])
    cdChunks.push(cd)
    cdSize += cd.length
  }

  outChunks.push(...cdChunks)
  outChunks.push(concat([
    u32(0x06054b50),
    u16(0), u16(0),
    u16(localFiles.length), u16(localFiles.length),
    u32(cdSize),
    u32(cdStart),
    u16(0)
  ]))

  return concat(outChunks)
}
